/**
 * @fileoverview Generic donut/pie chart paired with a breakdown table.
 * @description A ring chart with leader-line % labels that follow the ring's own silhouette
 * (a slice facing up gets a label above it, one facing right gets a label to the right), a
 * "Total" figure stamped in the donut hole, and a paired Category/Value/%-Share table beside or
 * below it. Ported near-verbatim from the InsightBox Banking design system's own
 * `SegmentPieChart`/`SegmentPieChartGroup` — the label-collision-avoidance geometry
 * (`computeLabelLayout`) is real, carefully-tuned math (documented inline) solving a genuinely
 * hard problem, not incidental complexity, so it's kept intact rather than simplified. Renamed
 * `PieBreakdownCard`/`PieBreakdownCardGroup` and restyled onto this app's own tokens since it has
 * no banking-specific coupling — any page needing a pie + breakdown table can reuse it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { CHART_GRID, CHART_SLATE, CHART_TEXT } from '@/shared/constants/chart-theme.constants'

// Floor for the chart box's rendered width, enforced below via min-width — not just a guess.
// Margin is a fixed pixel budget (font metrics don't scale with the box), so what fraction of the
// radius it costs depends on how big the box actually renders at. Pairing this constant with an
// actual CSS min-width means the box structurally cannot render smaller than this, so calibrating
// the margin against it is a real guarantee, not a guess. Two floors, not one: a standalone
// "right"-legend chart has nothing else competing for vertical space, so it can afford to be big.
// A "below"-legend chart is always paired with a legend table stacked directly underneath it, so
// it's sized smaller so a KPI row + chart + legend table can fit in one screen.
const MIN_BOX_SIZE_RIGHT = 420
const MIN_BOX_SIZE_BELOW = 300

export interface PieSegment {
  key: string
  label: string
  value: number
  pct: number
  color: string
}

export interface PieGroup {
  key: string
  label: string
  sublabel?: string
  segments: PieSegment[]
  categoryColumnLabel?: string
  valueColumnLabel?: string
}

const RADIAN = Math.PI / 180

/**
 * @description Font size and geometry constants for the pie's leader-line labels, scaled for
 * dense vs standard layout.
 * @param {boolean} dense - Whether the tighter/dense layout is active.
 */
function labelMetrics(dense: boolean) {
  const fontSize = dense ? 13 : 14.5
  return {
    fontSize,
    // How far past the ring's edge a label's natural point sits, along its own slice's angle — the
    // same distance for every segment, so labels fan out following the ring's own curve.
    pull: dense ? 22 : 26,
    // minGap is a center-to-center distance, but what a reader perceives is the edge-to-edge gap
    // between two label rows — roughly minGap minus the text's own line-height. Sized explicitly
    // off line-height plus a real target gap, confirmed by measuring actual rendered label boxes.
    minGap: fontSize * 1.25 + 14,
    // Rough average glyph width for a bold "XX.X%"-shaped label at this font size — used only to
    // size the reserved margin, not to draw anything, so an estimate is fine.
    textWidth: fontSize * 0.62 * 5,
  }
}

interface LabelPlacement {
  ux: number
  uy: number
  side: 1 | -1
  x: number
  y: number
  sx: number
  sy: number
}

/**
 * @description Computes a leader-line label position for every non-zero segment, following the
 * ring's own silhouette rather than snapping onto one shared vertical line, then declutters
 * vertically (per side of the ring) and re-clears the ring radius horizontally afterward. See the
 * file-level doc comment — this is carefully-tuned, documented geometry, ported as-is.
 * @param {PieSegment[]} segments - The pie's segments, in draw order.
 * @param {number} cx - The ring's center x.
 * @param {number} cy - The ring's center y.
 * @param {number} outerRadius - The ring's outer radius in px.
 * @param {boolean} dense - Whether the tighter/dense layout is active.
 * @returns {(LabelPlacement | null)[]} One placement per segment (`null` for a zero-value one).
 */
function computeLabelLayout(
  segments: PieSegment[],
  cx: number,
  cy: number,
  outerRadius: number,
  dense: boolean,
): (LabelPlacement | null)[] {
  const { pull, minGap } = labelMetrics(dense)
  const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1
  const reach = outerRadius + pull

  // Independently reproduce recharts' own angle math (startAngle=90, sweeping clockwise through a
  // full circle) so each segment's midpoint angle is known before any per-slice callback fires —
  // needed because collision avoidance requires seeing every label's natural position at once.
  let cumFraction = 0
  const raw = segments.map((segment) => {
    if (segment.value === 0) {
      cumFraction += segment.value / total
      return null
    }
    const fraction = segment.value / total
    const angleStart = 90 - 360 * cumFraction
    const angleEnd = 90 - 360 * (cumFraction + fraction)
    const midAngle = 90 - 360 * (cumFraction + fraction / 2)
    cumFraction += fraction
    const ux = Math.cos(-midAngle * RADIAN)
    const uy = Math.sin(-midAngle * RADIAN)
    const side: 1 | -1 = ux >= 0 ? 1 : -1
    return { ux, uy, side, angleStart, angleEnd }
  })

  // sx/sy (where the leader line touches the ring) start at each slice's own natural angle,
  // matching x/y below — every line begins straight. Only x/y ever move after this.
  const placements: (LabelPlacement | null)[] = raw.map((r) =>
    r
      ? {
          ux: r.ux,
          uy: r.uy,
          side: r.side,
          x: cx + r.ux * reach,
          y: cy + r.uy * reach,
          sx: cx + outerRadius * r.ux,
          sy: cy + outerRadius * r.uy,
        }
      : null,
  )

  // A dominant slice (spans nearly half the ring) whose midpoint sits near due-east/due-west lands
  // its label at the vertical center by coincidence, straight into the "Total" figure stamped in
  // the donut hole — pushed away unconditionally first, since it has no sibling for the sweep
  // below to space it away from.
  const centerExclusion = (dense ? 34 : 40) + (dense ? 24 : 30)
  const DOMINANT_FRACTION = 0.2
  placements.forEach((p, i) => {
    if (!p) return
    const seg = raw[i]!
    const fraction = Math.abs(seg.angleStart - seg.angleEnd) / 360
    if (fraction < DOMINANT_FRACTION) return
    if (Math.abs(p.y - cy) < centerExclusion) {
      p.y = cy + (p.uy < 0 ? -centerExclusion : centerExclusion)
    }
  })

  // Declutter each side independently: sort by y, then sweep down enforcing a minimum vertical
  // gap. This is the only place y changes — nothing after this point may touch it.
  for (const side of [1, -1] as const) {
    const onSide = placements
      .map((p, i) => (p && p.side === side ? i : -1))
      .filter((i) => i >= 0)
      .sort((a, b) => placements[a]!.y - placements[b]!.y)
    for (let k = 1; k < onSide.length; k++) {
      const prev = placements[onSide[k - 1]!]!
      const cur = placements[onSide[k]!]!
      if (cur.y - prev.y < minGap) cur.y = prev.y + minGap
    }
  }

  // Ring-clearance, restoring x only: with y now final, solve for the x that puts this point back
  // at exactly `reach` from center at that y, pulling it further out along the ring's curve
  // horizontally instead of moving it toward/away from the centerline vertically.
  placements.forEach((p) => {
    if (!p) return
    const dy = p.y - cy
    const minXSquared = reach * reach - dy * dy
    if (minXSquared > 0) {
      const minAbsX = Math.sqrt(minXSquared)
      if (Math.abs(p.x - cx) < minAbsX) p.x = cx + p.side * minAbsX
    }
  })

  // Re-anchor the ring point to the label's FINAL angle, not its original one, so a label nudged
  // sideways during decluttering still gets a straight line instead of one that visibly kinks
  // partway through. Clamped back to the slice's own arc (or center, if it doesn't fit at all) so
  // the anchor never lands ambiguously on a neighbor's seam.
  placements.forEach((p, i) => {
    if (!p) return
    const dx = p.x - cx
    const dy = p.y - cy
    const finalAngle = -Math.atan2(dy, dx) / RADIAN
    const { angleStart, angleEnd } = raw[i]!
    const lo = Math.min(angleStart, angleEnd)
    const hi = Math.max(angleStart, angleEnd)
    const mid = (lo + hi) / 2
    let a = finalAngle
    while (a - mid > 180) a -= 360
    while (a - mid < -180) a += 360
    const anchorAngle = a < lo || a > hi ? mid : a
    const rad = -anchorAngle * RADIAN
    p.sx = cx + outerRadius * Math.cos(rad)
    p.sy = cy + outerRadius * Math.sin(rad)
  })

  return placements
}

/**
 * @description Builds recharts' `label` renderer for a `<Pie>`, closing over the segment list so
 * the collision-avoidance layout is computed once (on first call) and reused for every segment.
 * @param {PieSegment[]} segments - The pie's segments, in draw order.
 * @param {boolean} dense - Whether the tighter/dense layout is active.
 */
function makeLabelRenderer(segments: PieSegment[], dense: boolean) {
  const { fontSize } = labelMetrics(dense)
  let layout: (LabelPlacement | null)[] | null = null
  // recharts' own label-renderer prop type is untyped/`any` in its public API.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (props: any) => {
    const { cx, cy, outerRadius, index } = props as {
      cx: number
      cy: number
      outerRadius: number
      index: number
    }
    layout ??= computeLabelLayout(segments, cx, cy, outerRadius, dense)
    const seg = segments[index]
    const p = layout[index]
    // A genuinely zero-value segment has no arc to anchor a line to — still shown in the legend,
    // just not stamped/pointed-to on the chart itself.
    if (!seg || !p) return null

    return (
      <g>
        <path
          d={`M${p.sx},${p.sy}L${p.x},${p.y}`}
          stroke={CHART_SLATE}
          strokeWidth={1}
          strokeLinecap="round"
          fill="none"
          opacity={0.55}
        />
        <circle cx={p.x} cy={p.y} r={3.5} fill={seg.color} stroke="#fff" strokeWidth={1.5} />
        <text
          x={p.x + (p.side >= 0 ? 8 : -8)}
          y={p.y}
          textAnchor={p.side >= 0 ? 'start' : 'end'}
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight={700}
          fill={CHART_TEXT}
        >
          {seg.pct.toFixed(2)}%
        </text>
      </g>
    )
  }
}

interface PieBreakdownCardProps {
  label?: string | undefined
  sublabel?: string | undefined
  segments: PieSegment[]
  valueFormatter: (value: number) => string
  totalLabel: string
  legendPosition: 'right' | 'below'
  dense?: boolean
  height?: number
  categoryColumnLabel?: string | undefined
  valueColumnLabel?: string | undefined
  shareColumnLabel?: string
}

/**
 * @component
 * @param {PieBreakdownCardProps} props - The pie's segments, formatting, and layout switches.
 * @param {string} [props.label] - An optional title above the chart.
 * @param {string} [props.sublabel] - An optional line under the title.
 * @param {PieSegment[]} props.segments - The segments to render, in draw order.
 * @param {(value: number) => string} props.valueFormatter - Formats a segment's raw value.
 * @param {string} props.totalLabel - The small caption above the total figure stamped in the
 * donut hole (e.g. "Total").
 * @param {'right' | 'below'} props.legendPosition - "right" for a single standalone pie (chart
 * left, legend right); "below" for pies shown side-by-side, each with its own legend underneath.
 * @param {boolean} [props.dense] - Tighter type scale/spacing. Off by default.
 * @param {number} [props.height] - Chart box height in px — width always fills its container, and
 * the ring is sized off whichever of the two is smaller.
 * @param {string} [props.categoryColumnLabel] - The table's category column header. Defaults to
 * "Category".
 * @param {string} [props.valueColumnLabel] - The table's value column header. Defaults to
 * "Value".
 * @param {string} [props.shareColumnLabel] - The table's %-share column header. Defaults to
 * "% Share".
 */
function PieBreakdownCard({
  label,
  sublabel,
  segments,
  valueFormatter,
  totalLabel,
  legendPosition,
  dense = false,
  height,
  categoryColumnLabel = 'Category',
  valueColumnLabel = 'Value',
  shareColumnLabel = '% Share',
}: PieBreakdownCardProps) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0)
  const h = height ?? (legendPosition === 'below' ? (dense ? 300 : 340) : dense ? 480 : 520)
  // When a caller asks for a specific size (e.g. two charts sharing a row), the box should
  // actually hold near that size — not float up to the much bigger standalone floor, which would
  // consume most of the row and leave the legend beside it squeezed.
  const minBoxSize =
    height != null
      ? Math.round(h * 0.9)
      : legendPosition === 'below'
        ? MIN_BOX_SIZE_BELOW
        : MIN_BOX_SIZE_RIGHT

  const { pull, textWidth } = labelMetrics(dense)
  const marginPx = pull + textWidth + 12
  const outerRadiusFrac = Math.max(0.38, 1 - marginPx / (minBoxSize / 2))
  const outerRadius = `${(outerRadiusFrac * 100).toFixed(1)}%`
  const innerRadius = `${(outerRadiusFrac * 0.55 * 100).toFixed(1)}%`

  return (
    <div className="flex flex-col gap-2">
      {(label || sublabel) && (
        <div>
          {label && (
            <p className={`font-semibold text-text ${dense ? 'text-sm' : 'text-base'}`}>{label}</p>
          )}
          {sublabel && <p className="text-sm text-label">{sublabel}</p>}
        </div>
      )}
      <div
        className={
          legendPosition === 'right'
            ? 'flex flex-col gap-6 lg:flex-row lg:items-center'
            : 'flex flex-col items-center gap-2'
        }
      >
        <div
          className={`relative aspect-square w-full ${legendPosition === 'right' ? 'lg:flex-[3]' : ''}`}
          style={{ maxWidth: h, minWidth: minBoxSize }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={segments}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={90}
                endAngle={-270}
                stroke="#fff"
                strokeWidth={2}
                labelLine={false}
                label={makeLabelRenderer(segments, dense)}
                isAnimationActive={false}
              >
                {segments.map((segment) => (
                  <Cell key={segment.key} fill={segment.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, _name, item) => {
                  // recharts types a tooltip payload entry's own `payload` (the underlying data
                  // point) as `any` in its own public API — there's no narrower type to reach for
                  // here, so this one assertion is the actual "the payload for a Pie's tooltip
                  // entry is a PieSegment" contract, not a shortcut around a real type.
                  const segment = item.payload as PieSegment
                  return [
                    `${valueFormatter(Number(value))} (${segment.pct.toFixed(2)}%)`,
                    segment.label,
                  ]
                }}
                contentStyle={{ borderRadius: 12, borderColor: CHART_GRID }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <span className="text-[10px] tracking-wide text-label uppercase">{totalLabel}</span>
            <span
              className={`leading-tight font-extrabold text-text ${
                legendPosition === 'below'
                  ? dense
                    ? 'text-lg'
                    : 'text-xl'
                  : dense
                    ? 'text-2xl'
                    : 'text-3xl'
              }`}
            >
              {valueFormatter(total)}
            </span>
          </div>
        </div>
        <div
          className={
            legendPosition === 'right'
              ? 'w-full lg:ml-auto lg:w-auto lg:max-w-[440px] lg:min-w-[260px] lg:flex-[2]'
              : 'w-full'
          }
        >
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th
                  className={`pb-1.5 text-left font-bold tracking-wide text-label uppercase ${dense ? 'text-xs' : 'text-sm'}`}
                >
                  {categoryColumnLabel}
                </th>
                <th
                  className={`pb-1.5 pl-3 text-right font-bold tracking-wide text-label uppercase ${dense ? 'text-xs' : 'text-sm'}`}
                >
                  {valueColumnLabel}
                </th>
                <th
                  className={`pb-1.5 pl-3 text-right font-bold tracking-wide text-label uppercase ${dense ? 'text-xs' : 'text-sm'}`}
                >
                  {shareColumnLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {segments.map((segment) => (
                <tr
                  key={segment.key}
                  className="border-b border-dotted border-border last:border-b-0"
                >
                  <td className={`${dense ? 'py-1.5 text-sm' : 'py-3 text-lg'}`}>
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className="h-3 w-3 shrink-0 rounded-sm"
                        style={{ background: segment.color }}
                      />
                      <span className="truncate font-semibold text-text">{segment.label}</span>
                    </div>
                  </td>
                  <td
                    className={`pl-3 text-right font-semibold whitespace-nowrap text-text ${dense ? 'py-1.5 text-sm' : 'py-3 text-lg'}`}
                  >
                    {valueFormatter(segment.value)}
                  </td>
                  <td
                    className={`pl-3 text-right font-bold whitespace-nowrap text-primary ${dense ? 'py-1.5 text-sm' : 'py-3 text-lg'}`}
                  >
                    {segment.pct.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

interface PieBreakdownCardGroupProps {
  groups: PieGroup[]
  valueFormatter: (value: number, groupKey: string) => string
  totalLabel: string
  dense?: boolean
}

/**
 * @description Two (or more) `PieBreakdownCard`s laid side by side, each with its own legend
 * underneath (`legendPosition="below"`) — for pages comparing two related breakdowns (e.g. income
 * mix and expense mix) in one card.
 * @param {PieBreakdownCardGroupProps} props - The groups to render and shared formatting.
 * @param {PieGroup[]} props.groups - Each group's own segments and column labels.
 * @param {(value: number, groupKey: string) => string} props.valueFormatter - Formats a segment's
 * raw value, given which group it belongs to (in case groups use different units).
 * @param {string} props.totalLabel - The small caption above each group's total figure.
 * @param {boolean} [props.dense] - Tighter type scale/spacing. Off by default.
 */
function PieBreakdownCardGroup({
  groups,
  valueFormatter,
  totalLabel,
  dense = false,
}: PieBreakdownCardGroupProps) {
  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-10 lg:grid-cols-2 lg:divide-x lg:divide-border">
      {groups.map((group) => (
        <div
          key={group.key}
          className="lg:[&:not(:first-child)]:pl-10 lg:[&:not(:last-child)]:pr-10"
        >
          <PieBreakdownCard
            label={group.label}
            sublabel={group.sublabel}
            segments={group.segments}
            valueFormatter={(value) => valueFormatter(value, group.key)}
            totalLabel={totalLabel}
            legendPosition="below"
            dense={dense}
            height={dense ? 460 : 500}
            categoryColumnLabel={group.categoryColumnLabel}
            valueColumnLabel={group.valueColumnLabel}
          />
        </div>
      ))}
    </div>
  )
}

export default PieBreakdownCard
export { PieBreakdownCardGroup }
